import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  where,
  serverTimestamp,
  getDoc,
  setDoc,
  writeBatch,
} from "firebase/firestore";

import { Platform } from "react-native";
import { db, auth, COLLECTIONS } from "../config/firebase";
import { NotificationPreferencesService } from "./notificationPreferencesService";
import { shouldNotifyMemberRole, isConfirmedRsvp } from "../utils/notificationTargeting";

// The push pipeline (firebase-functions/index.js's sendPushNotification)
// only forwards an allowlisted set of fields from a notification doc's
// nested `data` object to the device — the top-level event_id/club_id
// fields on the doc itself never reach the phone. App.js's notification-tap
// handler reads data.event_id to deep-link into EventDetailsScreen, so any
// event-related notification whose `data` is missing event_id silently
// can't deep-link, with no error anywhere to catch it. Call sites that
// don't go through sendEventNotification (which enforces this internally)
// should build their data object with this helper instead of hand-typing
// { event_id, club_id, type } and risking a typo or an omitted field.
export function buildEventNotificationData(eventId, { clubId, type, ...extra } = {}) {
  return {
    ...extra,
    ...(clubId ? { club_id: clubId } : {}),
    ...(type ? { type } : {}),
    event_id: eventId,
  };
}

export class NotificationService {
  // Save push token for user — uses a deterministic doc ID ({userId}_{platform}) so
  // duplicate calls structurally cannot create multiple documents (Firestore upsert).
  // Also deletes any legacy random-ID token documents for this user during migration.
  static async savePushToken(userId, token, platform = null) {
    try {
      const detectedPlatform =
        platform || (Platform.OS === "ios" ? "ios" : "android");

      // Deterministic doc ID — guarantees exactly one document per user per platform
      const docId = `${userId}_${detectedPlatform}`;

      // Get ALL existing tokens for this user
      const allTokensQuery = query(
        collection(db, COLLECTIONS.PUSH_TOKENS),
        where("user_id", "==", userId)
      );
      const allTokens = await getDocs(allTokensQuery);

      // Delete all token documents that are NOT the one we are about to upsert.
      // This covers: legacy random-ID docs, stale tokens for other platforms,
      // and any other duplicates created before deterministic IDs were introduced.
      const deletePromises = [];
      allTokens.forEach((docSnap) => {
        if (docSnap.id !== docId) {
          deletePromises.push(deleteDoc(docSnap.ref));
        }
      });

      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
      }

      // Check whether the deterministic doc already exists so we can preserve created_at
      const existingSnap = await getDoc(doc(db, COLLECTIONS.PUSH_TOKENS, docId));

      const tokenData = {
        user_id: userId,
        token,
        platform: detectedPlatform,
        updated_at: serverTimestamp(),
      };

      // Only include created_at for brand-new documents — merge: true won't
      // overwrite it on subsequent calls, but we omit it explicitly to be safe.
      if (!existingSnap.exists()) {
        tokenData.created_at = serverTimestamp();
      }

      await setDoc(doc(db, COLLECTIONS.PUSH_TOKENS, docId), tokenData, { merge: true });

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Save push token error:", error);
      return { error };
    }
  }

  // Get user's push tokens
  static async getUserPushTokens(userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.PUSH_TOKENS),
        where("user_id", "==", userId)
      );

      const querySnapshot = await getDocs(q);
      const tokens = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { data: tokens, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get user push tokens error:", error);
      return { data: null, error };
    }
  }

  // Get user's notifications
  static async getUserNotifications(userId, limitCount = 50) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NOTIFICATIONS),
        where("user_id", "==", userId),
        orderBy("created_at", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const notifications = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { data: notifications, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get user notifications error:", error);
      return { data: null, error };
    }
  }

  // Mark notification as read
  static async markNotificationAsRead(notificationId, userId) {
    try {
      // M7: Require userId for ownership verification
      if (!userId) {
        return { error: { message: "userId is required" } };
      }

      const notificationRef = doc(
        db,
        COLLECTIONS.NOTIFICATIONS,
        notificationId
      );

      const notificationDoc = await getDoc(notificationRef);

      if (!notificationDoc.exists()) {
        return { error: { message: "Notification not found" } };
      }

      // M7: Verify notification belongs to caller
      if (notificationDoc.data().user_id !== userId) {
        return { error: { message: "Unauthorized: notification does not belong to you" } };
      }

      await updateDoc(notificationRef, {
        read: true,
        read_at: serverTimestamp(),
      });

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Mark notification as read error:", error);
      return { error };
    }
  }

  // Mark all notifications as read
  static async markAllNotificationsAsRead(userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NOTIFICATIONS),
        where("user_id", "==", userId),
        where("read", "==", false)
      );

      const querySnapshot = await getDocs(q);

      for (const doc of querySnapshot.docs) {
        await updateDoc(doc.ref, {
          read: true,
          read_at: serverTimestamp(),
        });
      }

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Mark all notifications as read error:", error);
      return { error };
    }
  }

  // Delete notification
  static async deleteNotification(notificationId, userId) {
    try {
      // M7: Require userId for ownership verification
      if (!userId) {
        return { error: { message: "userId is required" } };
      }

      const notificationRef = doc(db, COLLECTIONS.NOTIFICATIONS, notificationId);
      const notificationDoc = await getDoc(notificationRef);

      if (!notificationDoc.exists()) {
        return { error: { message: "Notification not found" } };
      }

      // M7: Verify notification belongs to caller
      if (notificationDoc.data().user_id !== userId) {
        return { error: { message: "Unauthorized: notification does not belong to you" } };
      }

      await deleteDoc(notificationRef);
      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Delete notification error:", error);
      return { error };
    }
  }

  // Send notification to club members
  //
  // Perf (2026-09-15): the per-member fan-out below used to run strictly
  // sequentially — up to 2 preference reads + 1 write per member, one
  // member at a time — which is the dominant cause of slow push delivery
  // for larger clubs (N members => up to 3N sequential round trips before
  // this function even returned). It now does one synchronous filtering
  // pass to build the candidate list, then evaluates+writes every
  // candidate CONCURRENTLY via Promise.all, with a single
  // getRawPreferences() read per candidate shared between the
  // enabled-check and the mute-check (previously two separate reads of the
  // same doc). The gating LOGIC (role targeting, enabled default, chat-mute
  // vs general-notification distinction, mention bypass hint, dedup ID
  // scheme) is unchanged — only the I/O shape changed.
  static async sendClubNotification(clubId, notificationData, excludeUserId = null, options = {}) {
    try {
      // Get all club members
      const membersQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId)
      );
      const membersSnapshot = await getDocs(membersQuery);

      // Deduplicate member user IDs — duplicate membership records
      // would otherwise cause duplicate notifications for the same user.
      // Also apply excludeUserId and role targeting here, synchronously,
      // so only genuine candidates go on to the async per-user work below.
      const seenUserIds = new Set();
      const candidateIds = [];
      for (const memberDoc of membersSnapshot.docs) {
        const memberData = memberDoc.data();
        const userId = memberData.user_id;

        if (seenUserIds.has(userId)) continue;
        seenUserIds.add(userId);

        // Skip the sender (e.g. for chat messages)
        if (excludeUserId && userId === excludeUserId) continue;

        // Role-targeted notifications (e.g. E-Board chat): when options.roles
        // is provided, only members whose role is in that list are notified.
        // No options.roles => unrestricted (preserves all-members behaviour).
        if (!shouldNotifyMemberRole(memberData.role, options.roles)) continue;

        candidateIds.push(userId);
      }

      const dataType = notificationData.data?.type;
      const senderId = auth.currentUser?.uid || null;

      // Perf P5 (2026-09-23): the notification docs are written with
      // writeBatch (≤450 per commit) instead of one awaited setDoc/addDoc per
      // member — one round trip per chunk instead of one per member. Each
      // Promise.all callback below now only DECIDES and BUILDS its doc; the
      // writes are collected here and committed after. Doc ids, shapes,
      // dedup keys and mention flags are exactly what the per-member writes
      // produced, so the sendPushNotification trigger sees identical docs.
      const pendingWrites = []; // { ref, notification }

      const results = await Promise.all(candidateIds.map(async (userId) => {
        // One read serves both the enabled-check and the mute-check below
        // (previously two separate getDoc calls on the same document).
        //
        // Perf P5: firestore.rules only lets the OWNER read
        // user_notification_preferences/{uid}, so for every recipient other
        // than the sender this read has always been a guaranteed
        // permission-denied round trip that getRawPreferences swallowed into
        // "no prefs" (= enabled, unmuted). Skipping it for other users
        // changes no outcome — the sendPushNotification trigger (Admin SDK)
        // enforces the club toggle and chat mutes server-side — and removes
        // N failed reads from the phone. The sender's own row (the one read
        // that CAN succeed, and whose result CAN suppress their own doc) is
        // still read, so self-notification behavior is unchanged.
        const { data: rawPrefs } =
          userId === senderId
            ? await NotificationPreferencesService.getRawPreferences(userId)
            : { data: null };

        // Check if user has notifications enabled for this club
        const isEnabled = NotificationPreferencesService.computeNotificationEnabled(rawPrefs, clubId);
        if (!isEnabled) {
          if (__DEV__) console.log(
            `Skipping notification for user ${userId} - notifications disabled for club ${clubId}`
          );
          return null;
        }

        // Check if user has muted this specific chat. Phase B (docs/
        // superpowers/specs/2026-07-22-customizable-chats-design.md):
        // consolidated chat_settings check (muted + muted_until expiry) with
        // a fallback to the legacy chat_mute_preferences field for
        // not-yet-migrated users. Only applies to actual chat message
        // notifications — muting a club's chat must NOT also suppress
        // unrelated club notifications (new_event, club_application, etc.),
        // matching sendEventNotification's isChatMessage-only gate below.
        // KAN-41: a mentioned recipient's chat-mute is BYPASSED — but only
        // after sendPushNotification's own independent Admin-SDK membership
        // re-check (see .planning/chat-revamp/patch-mentions-push.md). This
        // is a client-side hint only (data.mention/mention_index on the
        // notification doc); the actual bypass decision is made entirely
        // server-side, since notifications/{id}'s create rule lets any
        // sender_id name any user_id as recipient.
        const isMentioned = options.mentionedUserIds?.includes(userId);
        if (dataType === "club_message") {
          const isMuted = NotificationPreferencesService.computeChatMuted(rawPrefs, "club", clubId);
          if (isMuted && !isMentioned) {
            if (__DEV__) console.log(
              `Skipping notification for user ${userId} - chat muted for club ${clubId}`
            );
            return null;
          }
        }

        const notification = {
          user_id: userId,
          sender_id: senderId,
          club_id: clubId,
          type: "club_notification",
          title: notificationData.title,
          message: notificationData.message,
          data: {
            ...(notificationData.data || {}),
            ...(isMentioned
              ? {
                  mention: true,
                  mention_index: options.mentionedUserIds.indexOf(userId),
                }
              : {}),
          },
          created_at: serverTimestamp(),
          read: false,
        };

        // For one-time notifications (new_event), use setDoc with a deterministic
        // ID so duplicate calls overwrite instead of appending. Chat messages use
        // addDoc since each message IS a unique notification.
        // (dataType already declared above for the chat-mute gate.)
        // application_id (2026-09-23): a per-application source so repeat
        // applications to one club get distinct dedup ids (see
        // ClubDetailsScreen's club_application call). Callers that don't
        // pass it keep the exact ids they always had.
        const sourceId =
          notificationData.data?.event_id ||
          notificationData.data?.application_id ||
          notificationData.data?.club_id;
        if (dataType && dataType !== "club_message" && sourceId) {
          const dedupId = `${dataType}_${sourceId}_${userId}`;
          const ref = doc(db, COLLECTIONS.NOTIFICATIONS, dedupId);
          pendingWrites.push({ ref, notification });
          return { id: dedupId, ...notification };
        }
        if (__DEV__ && dataType && dataType !== "club_message" && !sourceId) {
          console.warn("[NotificationService] addDoc fallback: missing sourceId for type=" + dataType);
        }
        // Same as addDoc: a fresh auto-id ref, set in the batch.
        const ref = doc(collection(db, COLLECTIONS.NOTIFICATIONS));
        pendingWrites.push({ ref, notification });
        return { id: ref.id, ...notification };
      }));

      // Commit in chunks (Firestore caps a batch at 500 writes; 450 leaves
      // headroom).
      //
      // Failure semantics (security-pass note, 2026-09-23): a batch is
      // atomic, so ONE denied write would drop the whole chunk — whereas the
      // old per-member setDoc/addDoc writes each succeeded or failed on their
      // own. A denied write is real for dedup-id docs: notifications' update
      // rule is recipient-only, so a sender re-setting an EXISTING
      // `${type}_${sourceId}_${userId}` doc (a repeat notification with the
      // same sourceId) is permission-denied for every recipient but
      // themselves. new_event ids embed the unique event id and never
      // pre-exist, but the club_application path reuses one id per
      // (club, admin) for every applicant (pre-existing behavior, logged
      // separately). To keep the outcome identical to the old code on that
      // path, a failed chunk falls back to individual writes and reports the
      // first individual failure the way the old Promise.all did — the
      // happy path is still one round trip per chunk.
      const BATCH_CHUNK = 450;
      let firstWriteError = null;
      for (let start = 0; start < pendingWrites.length; start += BATCH_CHUNK) {
        const chunk = pendingWrites.slice(start, start + BATCH_CHUNK);
        const batch = writeBatch(db);
        for (const { ref, notification } of chunk) {
          batch.set(ref, notification);
        }
        try {
          await batch.commit();
        } catch (batchError) {
          const settled = await Promise.allSettled(
            chunk.map(({ ref, notification }) => setDoc(ref, notification))
          );
          const rejected = settled.find((r) => r.status === "rejected");
          if (rejected && !firstWriteError) firstWriteError = rejected.reason || batchError;
        }
      }
      if (firstWriteError) throw firstWriteError;

      return { data: results.filter(Boolean), error: null };
    } catch (error) {
      if (__DEV__) console.error("Send club notification error:", error);
      return { data: null, error };
    }
  }

  // Notify an explicit list of user IDs (sub-chat member_ids — already
  // resolved on the client, unlike club_members which sendClubNotification
  // above queries fresh). The parent club's notification toggle and the
  // per-sub-chat mute (the bell in SubChatScreen) are BOTH enforced server-side
  // in the sendPushNotification trigger (Admin SDK): a sender's client can't
  // read a recipient's preferences (Firestore rules allow only the owner), so
  // the mute can't be applied here. The notification doc carries data.subchat_id
  // for that server-side check.
  // `mentionedUserIds` (KAN-41): this function has no client-side mute check
  // at all (see the comment below), so there's no "continue" to except — the
  // only thing this option does is mark each mentioned recipient's own doc
  // with data.mention/mention_index, exactly like sendClubNotification/
  // sendEventNotification, for sendPushNotification's own server-side bypass
  // decision (see .planning/chat-revamp/patch-mentions-push.md).
  // Perf (2026-09-15): candidates evaluated+written concurrently, same as
  // sendClubNotification/sendEventNotification above.
  static async sendSubChatNotification(memberIds, notificationData, excludeUserId, clubId, mentionedUserIds = []) {
    try {
      const seenUserIds = new Set();
      const candidateIds = [];
      for (const userId of memberIds) {
        if (!userId || seenUserIds.has(userId)) continue;
        seenUserIds.add(userId);
        if (excludeUserId && userId === excludeUserId) continue;
        candidateIds.push(userId);
      }

      const senderId = auth.currentUser?.uid || null;

      const results = await Promise.all(candidateIds.map(async (userId) => {
        const { data: isEnabled } =
          await NotificationPreferencesService.isNotificationEnabled(userId, clubId);
        if (!isEnabled) return null;

        const isMentioned = mentionedUserIds?.includes(userId);
        const notification = {
          user_id: userId,
          sender_id: senderId,
          club_id: clubId,
          type: "subchat_message",
          title: notificationData.title,
          message: notificationData.message,
          data: {
            ...(notificationData.data || {}),
            ...(isMentioned
              ? { mention: true, mention_index: mentionedUserIds.indexOf(userId) }
              : {}),
          },
          created_at: serverTimestamp(),
          read: false,
        };
        const docRef = await addDoc(
          collection(db, COLLECTIONS.NOTIFICATIONS),
          notification
        );
        return { id: docRef.id, ...notification };
      }));

      return { data: results.filter(Boolean), error: null };
    } catch (error) {
      if (__DEV__) console.error("Send sub-chat notification error:", error);
      return { data: null, error };
    }
  }

  // Send notification to event attendees
  // `options.mentionedUserIds` (KAN-41) mirrors sendClubNotification's own
  // option — see that method's comment for the full contract.
  //
  // Perf (2026-09-15): same restructuring as sendClubNotification above —
  // synchronous candidate filtering first, then one getRawPreferences() read
  // per candidate shared between the mute-check and the enabled-check, all
  // candidates evaluated+written CONCURRENTLY via Promise.all instead of
  // one attendee at a time. Gating logic is unchanged.
  static async sendEventNotification(eventId, notificationData, excludeUserId = null, options = {}) {
    try {
      // Get all event attendees
      const attendeesQuery = query(
        collection(db, COLLECTIONS.EVENT_ATTENDEES),
        where("event_id", "==", eventId)
      );
      const attendeesSnapshot = await getDocs(attendeesQuery);

      // Deduplicate attendee user IDs — duplicate attendee records
      // would otherwise cause duplicate notifications for the same user.
      const seenUserIds = new Set();
      const candidateIds = [];
      for (const attendeeDoc of attendeesSnapshot.docs) {
        const attendeeData = attendeeDoc.data();
        const userId = attendeeData.user_id;

        if (seenUserIds.has(userId)) continue;
        seenUserIds.add(userId);

        // Skip the sender
        if (excludeUserId && userId === excludeUserId) {
          continue;
        }

        // Bug-2 fix: event_attendees holds both RSVPs and check-ins. Event
        // alerts must go ONLY to confirmed RSVPs (status === "attending"),
        // never to non-attending / stale rows.
        if (!isConfirmedRsvp(attendeeData)) continue;

        candidateIds.push(userId);
      }

      const dataType = notificationData.data?.type;
      const senderId = auth.currentUser?.uid || null;

      const results = await Promise.all(candidateIds.map(async (userId) => {
        // One read serves both the mute-check and the enabled-check below.
        const { data: rawPrefs } = await NotificationPreferencesService.getRawPreferences(userId);

        // Phase B (docs/superpowers/specs/2026-07-22-customizable-chats-design.md):
        // chat messages (event_message) are gated SOLELY by the consolidated
        // chat_settings mute check, which already has its own correct legacy
        // event_preferences fallback (with the right muted/enabled polarity).
        // They deliberately do NOT also go through computeEventNotificationEnabled
        // below: historically the old EventChatScreen mute toggle wrote
        // event_preferences[eventId] AS chat-mute, so applying both gates to
        // the same legacy field double-counts it — a user with a stale
        // event_preferences[eventId] === false would have chat pushes
        // permanently blocked by this gate regardless of what they select in
        // the new mute-duration menu, and a timed mute would never expire
        // (the stale legacy value short-circuits before muted_until is ever
        // evaluated). Non-chat event notifications (event_reminder, etc.)
        // still respect the general per-event notification opt-out, matching
        // sendClubNotification's isChatMessage-only gate above.
        // KAN-41 — see sendClubNotification's identical comment on this
        // pattern for the full contract.
        const isMentioned = options.mentionedUserIds?.includes(userId);
        if (dataType === "event_message") {
          const isMuted = NotificationPreferencesService.computeChatMuted(rawPrefs, "event", eventId);
          if (isMuted && !isMentioned) {
            if (__DEV__) console.log(
              `Skipping notification for user ${userId} - chat muted for event ${eventId}`
            );
            return null;
          }
        } else {
          // Check if user has notifications enabled for this event
          const isEnabled = NotificationPreferencesService.computeEventNotificationEnabled(rawPrefs, eventId);
          if (!isEnabled) {
            if (__DEV__) console.log(
              `Skipping notification for user ${userId} - notifications disabled for event ${eventId}`
            );
            return null;
          }
        }

        const notification = {
          user_id: userId,
          sender_id: senderId,
          event_id: eventId,
          type: "event_notification",
          title: notificationData.title,
          message: notificationData.message,
          // event_id is guaranteed here (not just left to the caller's data
          // object) — this function already receives eventId as a required
          // parameter, so a notification it sends should never be able to
          // reach a device without the id needed for deep-linking to work.
          data: {
            ...(notificationData.data || {}),
            event_id: eventId,
            ...(isMentioned
              ? {
                  mention: true,
                  mention_index: options.mentionedUserIds.indexOf(userId),
                }
              : {}),
          },
          created_at: serverTimestamp(),
          read: false,
        };

        // For one-time event notifications (e.g. event_reminder, event_update),
        // use setDoc with a deterministic ID so duplicate calls overwrite instead
        // of appending. Chat messages (event_message) use addDoc since each
        // message IS a unique notification.
        // (dataType already declared above for the chat-mute gate.)
        const sourceId = notificationData.data?.event_id || notificationData.data?.club_id || eventId;
        if (dataType && dataType !== "event_message" && sourceId) {
          const dedupId = `${dataType}_${sourceId}_${userId}`;
          await setDoc(doc(db, COLLECTIONS.NOTIFICATIONS, dedupId), notification);
          return { id: dedupId, ...notification };
        }
        if (__DEV__ && dataType && dataType !== "event_message" && !sourceId) {
          console.warn("[NotificationService] addDoc fallback: missing sourceId for type=" + dataType);
        }
        const docRef = await addDoc(
          collection(db, COLLECTIONS.NOTIFICATIONS),
          notification
        );
        return { id: docRef.id, ...notification };
      }));

      return { data: results.filter(Boolean), error: null };
    } catch (error) {
      if (__DEV__) console.error("Send event notification error:", error);
      return { data: null, error };
    }
  }

  /**
   * Get all super_admin users
   */
  static async getSuperAdminUsers() {
    try {
      const usersQuery = query(
        collection(db, COLLECTIONS.USERS),
        where("role", "==", "super_admin")
      );
      const usersSnapshot = await getDocs(usersQuery);

      const superAdmins = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { data: superAdmins, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get super admin users error:", error);
      return { data: null, error };
    }
  }

  /**
   * Send notification to all super_admins
   * Only sends to super_admins who have the specific notification type enabled
   */
  static async sendSuperAdminNotification(
    notificationType,
    notificationData
  ) {
    try {
      // Get all super_admin users
      const { data: superAdmins, error: superAdminsError } =
        await this.getSuperAdminUsers();

      if (superAdminsError || !superAdmins || superAdmins.length === 0) {
        if (__DEV__) console.log("No super_admin users found");
        return { data: [], error: null };
      }

      const notifications = [];

      for (const superAdmin of superAdmins) {
        // Check if this super_admin has notifications enabled for this type
        let isEnabled = true;
        if (notificationType === "admin_requests") {
          const { data: enabled } =
            await NotificationPreferencesService.isAdminRequestNotificationEnabled(
              superAdmin.id
            );
          isEnabled = enabled;
        } else if (notificationType === "club_creation_requests") {
          const { data: enabled } =
            await NotificationPreferencesService.isClubCreationRequestNotificationEnabled(
              superAdmin.id
            );
          isEnabled = enabled;
        }

        if (!isEnabled) {
          if (__DEV__) console.log(
            `Skipping notification for super_admin ${superAdmin.id} - ${notificationType} notifications disabled`
          );
          continue;
        }

        const notification = {
          user_id: superAdmin.id,
          sender_id: auth.currentUser?.uid || null,
          type: `super_admin_${notificationType}`,
          title: notificationData.title,
          message: notificationData.message,
          data: notificationData.data || {},
          created_at: serverTimestamp(),
          read: false,
        };

        // Use deterministic doc ID to prevent duplicate notifications
        // when the same request triggers multiple calls.
        const sourceId = notificationData.data?.application_id
          || notificationData.data?.club_id
          || notificationData.data?.user_id;
        if (sourceId) {
          const dedupId = `super_admin_${notificationType}_${sourceId}_${superAdmin.id}`;
          await setDoc(doc(db, COLLECTIONS.NOTIFICATIONS, dedupId), notification);
          notifications.push({ id: dedupId, ...notification });
        } else {
          if (__DEV__) console.warn("[NotificationService] addDoc fallback: missing sourceId for type=" + `super_admin_${notificationType}`);
          const docRef = await addDoc(
            collection(db, COLLECTIONS.NOTIFICATIONS),
            notification
          );
          notifications.push({ id: docRef.id, ...notification });
        }
      }

      return { data: notifications, error: null };
    } catch (error) {
      if (__DEV__) console.error("Send super admin notification error:", error);
      return { data: null, error };
    }
  }
}
